import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { BehaviorSubject, catchError, filter, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { LanguageStore } from '../stores/LanguageStore';
import { IdentityStore } from '../../features/auth/core/stores/IdentityStore';
import { AuthService } from '../../features/auth/core/services/AuthService';
import { environment } from '../../../environments/environment';
import { AppStore } from '../stores/AppStore';
import { TerminalsStore } from '../../features/terminals/core/stores/TerminalsStore';
import { ApiErrorCodes } from '../enums/ErrorCodes';
import { NON_BLOCKING } from '../http/non-blocking.context';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  private _isRefreshing = false;
  private readonly _refreshToken$ = new BehaviorSubject<string>(null);
  private readonly _endpointsWithoutToken = [
    '/auth/signin',
    '/auth/login-with-token',
    '/tools/languages',
    '/tools/snippets/all',
  ];
  private readonly _endpointsWithoutLang = [];
  private readonly _identityStore = inject(IdentityStore);
  private readonly _authService = inject(AuthService);
  private readonly _languageStore = inject(LanguageStore);
  private readonly _terminalsStore = inject(TerminalsStore);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const headersConfig = {
      'Accept': 'application/json',
      'X-app': environment.app,
      'x-version-prefix': environment.versionPrefix,
      'x-gui-version': environment.version,
    };

    if (this._terminalsStore.selectedTerminal()) {
      headersConfig['TerminalId'] = this._terminalsStore.selectedTerminal().id.toString();
      headersConfig['CinemaId'] = this._terminalsStore.changedCinema()?.id.toString() ??
        this._terminalsStore.selectedTerminal().cinemaId.toString();
      headersConfig['SessionId'] = this._terminalsStore.selectedTerminal().currentSession?.id.toString() ?? '';
      headersConfig['WorkerNumber'] = this._terminalsStore.selectedTerminal().worker?.number ?? '';
      headersConfig['MandatorId'] = this._terminalsStore.selectedTerminal().mandator.id.toString();
      headersConfig['BasketId'] = this._terminalsStore.selectedTerminal().currentSession?.currentBasketId?.toString()
        ?? '';
    }

    if (!this._endpointsWithoutLang.find(endpoint => req.url.includes(endpoint))) {
      const currentLang = this._languageStore.language()?.key;

      if (currentLang) {
        headersConfig['X-Lang'] = currentLang;
      }
    }

    if (!this._endpointsWithoutToken.find(endpoint => req.url.includes(endpoint))) {
      const identity = this._identityStore.identity() ?? this._identityStore.tmpIdentity();

      if (identity) {
        const { refreshToken, accessToken } = identity;
        headersConfig['Authorization'] = `Bearer ${req.url.includes('refresh-token') ? refreshToken : accessToken}`;
      }

      const request = req.clone({
        setHeaders: headersConfig
      });

      return next.handle(request).pipe(
        tap(data => {
          if (data instanceof HttpResponse) {
            this.setVersion(data);
          }
        }),
        catchError(error => {
          if (error instanceof HttpErrorResponse) {
            this.setVersion(error);
          }

          if (req.context.get(NON_BLOCKING)) {
            return of(new HttpResponse<any>({ status: 204, body: null }));
          }

          if (req.url.includes('refresh-token')) {
            this._authService.logout(true);

            return throwError(() => error);
          } else {
            if (error.status === 401) {
              return this.handle401Error(request, next);
            } else {
              if (error.error?.message === ApiErrorCodes.InvalidSessionStatus) {
                this._authService.logout();
              }

              return throwError(() => error);
            }
          }
        })
      );
    } else {
      const request = req.clone({
        setHeaders: headersConfig
      });

      return next.handle(request).pipe(
        tap(data => {
          if (data instanceof HttpResponse) {
            this.setVersion(data);
          }
        }),
        catchError(error => {
          if (error instanceof HttpErrorResponse) {
            this.setVersion(error);
          }

          if (req.context.get(NON_BLOCKING)) {
            return of(new HttpResponse<any>({ status: 204, body: null }));
          }

          return throwError(() => error);
        })
      );
    }
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this._isRefreshing) {
      this._isRefreshing = true;
      this._refreshToken$.next(null);

      return this._authService.refreshToken().pipe(
        switchMap(({ accessToken }) => {
          this._isRefreshing = false;
          this._identityStore.identity.update(identity => {
            return {
              ...identity,
              accessToken
            };
          });

          this._refreshToken$.next(accessToken);

          return next.handle(this.appendToken(request, accessToken));
        }),
        catchError((error) => {
          this._authService.logout();

          return throwError(() => error);
        })
      );
    } else {
      return this._refreshToken$.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.appendToken(request, token)))
      );
    }
  }

  private appendToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  private setVersion(data: HttpErrorResponse | HttpResponse<any>): void {
    const apiVersion = data.headers.get('x-response-api-version') ?? '';
    AppStore.version.set(`${environment.version}.${apiVersion.replace('dev-', '')}`);
  }
}
